import { TableRow, TableCell, Box, IconButton } from '@mui/material';
import { ArrowUpward, ArrowDownward, UnfoldMore as UnsortedIcon } from '@mui/icons-material';
import { flexRender } from '@tanstack/react-table';
import type { HeaderGroup } from '@tanstack/react-table';
import { getColumnWidth, isUtilityGridColumn } from '../utils';
import React, { useState, useCallback, useEffect } from 'react';

type DataGridHeaderProps<T> = {
  headerGroup: HeaderGroup<T>;
  headerRowRef: React.RefObject<HTMLTableRowElement | null>;
  onColumnReorder?: (draggedId: string, targetId: string) => void;
  hiddenColumnIds?: Set<string>;
};

export const DataGridHeader = <T,>({
  headerGroup,
  headerRowRef,
  onColumnReorder,
  hiddenColumnIds,
}: DataGridHeaderProps<T>) => {
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [resizingColumnId, setResizingColumnId] = useState<string | null>(null);

  const stopResizing = useCallback(() => {
    setResizingColumnId(null);
  }, []);

  return (
    <TableRow key={`${headerGroup.id}-header`} ref={headerRowRef}>
      {headerGroup.headers
        .filter((header) => !hiddenColumnIds?.has(header.column.id))
        .map((header) => {
          const columnId = header.column.id;
          const isCenteredCol = isUtilityGridColumn(columnId);
          const isResizingThisColumn = resizingColumnId === columnId;
          const isDraggable = !isCenteredCol && !!onColumnReorder && !isResizingThisColumn;

          return (
            <TableCell
              key={header.id}
              data-col-id={columnId}
              data-draggable={isDraggable}
              draggable={isDraggable}
              onDragStart={(e) => {
                if (!isDraggable) return;

                e.dataTransfer.effectAllowed = 'copyMove';
                e.dataTransfer.setData('text/plain', columnId);
                e.dataTransfer.setData('text/column-id', columnId);
                e.dataTransfer.setData('text/group-field', columnId);
              }}
              onDragEnter={(e) => {
                if (!isDraggable) return;
                e.preventDefault();
              }}
              onDragOver={(e) => {
                if (!isDraggable) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverId !== columnId) {
                  setDragOverId(columnId);
                }
              }}
              onDragLeave={(e) => {
                if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as Node)) {
                  return;
                }
                if (dragOverId === columnId) {
                  setDragOverId(null);
                }
              }}
              onDrop={(e) => {
                if (!isDraggable) return;
                e.preventDefault();
                e.stopPropagation();

                const draggedId =
                  e.dataTransfer.getData('text/column-id') || e.dataTransfer.getData('text/plain');

                setDragOverId(null);

                if (draggedId && draggedId !== columnId) {
                  onColumnReorder?.(draggedId, columnId);
                }
              }}
              onDragEnd={() => {
                setDragOverId(null);
              }}
              sx={{
                ...(columnId === '_navigate' && {
                  padding: 0,
                  border: 'none',
                }),
                fontWeight: 'bold',
                backgroundColor: (theme) =>
                  dragOverId === columnId
                    ? theme.palette.action.selected
                    : theme.palette.mode === 'dark'
                      ? theme.palette.background.paper
                      : theme.palette.background.default,
                color: (theme) =>
                  theme.palette.mode === 'dark'
                    ? theme.palette.text.secondary
                    : theme.palette.text.primary,
                cursor: header.column.getCanSort() ? 'pointer' : 'default',
                userSelect: 'none',
                width: getColumnWidth(header.column),
                ...(header.column.columnDef.meta
                  ? {}
                  : {
                      minWidth: `${header.column.columnDef.minSize || 50}px`,
                      maxWidth: `${header.column.columnDef.maxSize || 1000}px`,
                    }),
                position: 'sticky',
                top: 0,
                zIndex: 11,
                padding: isCenteredCol ? '0 2px' : '2px 8px',
                textAlign: isCenteredCol ? 'center' : undefined,
                borderLeft: (theme) =>
                  dragOverId === columnId ? `2px solid ${theme.palette.primary.main}` : undefined,
              }}
              onClick={header.column.getToggleSortingHandler()}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: isCenteredCol ? 'center' : undefined,
                  gap: 0.5,
                  pointerEvents: 'none',
                }}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
                {header.column.getCanSort() && (
                  <IconButton size="small" sx={{ ml: 0.5, pointerEvents: 'auto' }}>
                    {header.column.getIsSorted() === 'asc' ? (
                      <ArrowUpward fontSize="small" />
                    ) : header.column.getIsSorted() === 'desc' ? (
                      <ArrowDownward fontSize="small" />
                    ) : (
                      <UnsortedIcon fontSize="small" />
                    )}
                  </IconButton>
                )}
              </Box>
              {header.column.getCanResize() && (
                <Box
                  draggable={false}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setResizingColumnId(columnId);
                    header.getResizeHandler()(e);
                  }}
                  onTouchStart={(e) => {
                    setResizingColumnId(columnId);
                    header.getResizeHandler()(e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    height: '100%',
                    width: '5px',
                    cursor: 'col-resize',
                    userSelect: 'none',
                    touchAction: 'none',
                    '&:hover': { backgroundColor: 'primary.main' },
                    backgroundColor: header.column.getIsResizing() ? 'primary.main' : 'transparent',
                  }}
                />
              )}
            </TableCell>
          );
        })}
      <ResizeEndListener active={resizingColumnId !== null} onEnd={stopResizing} />
    </TableRow>
  );
};

const ResizeEndListener = ({ active, onEnd }: { active: boolean; onEnd: () => void }) => {
  useEffect(() => {
    if (!active) return;
    const handler = () => onEnd();
    window.addEventListener('mouseup', handler);
    window.addEventListener('touchend', handler);
    return () => {
      window.removeEventListener('mouseup', handler);
      window.removeEventListener('touchend', handler);
    };
  }, [active, onEnd]);

  return null;
};
